import React, { useState, useEffect } from 'react';
import { Search, ChevronDown, ChevronUp, Check, RotateCcw, Info, Sparkles, Zap, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/TestAuthContext';
import { api } from '../utils/api';
import { FinancialValue } from './shared/FinancialValue';
import { formatCurrency } from '../utils/financialFormat';

interface Template {
  id: string;
  name: string;
  description: string;
  icon: string;
  business_types: string[];
  gross_margin_avg: number;
  net_margin_avg: number;
}

interface TemplateDetails {
  id: string;
  name: string;
  description: string;
  icon: string;
  defaults: {
    cogs_pct: number;
    payroll_pct: number;
    rent_monthly: number;
    utilities_monthly: number;
    insurance_monthly: number;
    marketing_pct: number;
    supplies_pct: number;
    other_expenses_pct: number;
  };
  benchmarks: {
    gross_margin_low: number;
    gross_margin_avg: number;
    gross_margin_high: number;
    net_margin_low: number;
    net_margin_avg: number;
    net_margin_high: number;
    labor_cost_pct: number;
    rent_pct: number;
  };
  common_expense_categories: string[];
  tips: string[];
}

interface AppliedTemplateData {
  template_id: string;
  template_name: string;
  template_icon: string;
  applied_to_year: number;
  financial_data: any;
  reconstructed_pl: any;
  defaults_used: any;
  benchmarks: any;
  tips: string[];
  common_categories: string[];
  seasonal_adjustments?: Record<string, number>;
}

interface TemplateSelectorProps {
  businessType?: string;
  onApplyTemplate: (templateData: AppliedTemplateData) => void;
  onReset?: () => void;
  currentRevenue?: number;
  coverageYear?: number;
  appliedTemplateId?: string;
  compact?: boolean;
}

// Helper to generate year range (4 years ending with coverage year).
// Returns [] for sentinel coverageYear=-1 (caller didn't supply one) so
// downstream UI shows an empty/disabled state instead of phantom years.
const getYearRange = (coverageYear: number): number[] => {
  if (!Number.isFinite(coverageYear) || coverageYear < 1970 || coverageYear > 2100) {
    return [];
  }
  return [coverageYear - 3, coverageYear - 2, coverageYear - 1, coverageYear];
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TemplateSelector({
  businessType,
  onApplyTemplate,
  onReset,
  currentRevenue = 0,
  // Caller MUST pass coverageYear derived from claim data.  We keep a
  // non-functional default (-1) and short-circuit the year-range UI below,
  // so we don't silently invent a phantom [this-year-3 ... this-year] window.
  coverageYear = -1,
  appliedTemplateId,
  compact = false
}: TemplateSelectorProps) {
  const { getToken } = useAuth();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  
  // Multi-year revenue inputs
  const yearRange = getYearRange(coverageYear);
  const [yearlyRevenue, setYearlyRevenue] = useState<Record<number, string>>(() => {
    const initial: Record<number, string> = {};
    yearRange.forEach(year => {
      initial[year] = '';
    });
    return initial;
  });
  
  const [searchQuery, setSearchQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(!compact);
  const [previewData, setPreviewData] = useState<any>(null);
  
  // Growth rate calculation helper
  const [useGrowthRate, setUseGrowthRate] = useState(false);
  const [baseRevenue, setBaseRevenue] = useState('');
  const [growthRate, setGrowthRate] = useState('5');

  // Fetch templates on mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  // Update yearly revenue when coverage year changes
  useEffect(() => {
    const newYearRange = getYearRange(coverageYear);
    setYearlyRevenue(prev => {
      const updated: Record<number, string> = {};
      newYearRange.forEach(year => {
        updated[year] = prev[year] || '';
      });
      return updated;
    });
  }, [coverageYear]);

  // Apply growth rate to calculate yearly revenues
  const applyGrowthRate = () => {
    if (!baseRevenue || parseFloat(baseRevenue) <= 0) return;
    
    const base = parseFloat(baseRevenue);
    const rate = parseFloat(growthRate) / 100;
    const newRevenues: Record<number, string> = {};
    
    // Work backwards from coverage year
    yearRange.forEach((year, index) => {
      const yearsBack = yearRange.length - 1 - index;
      const calculatedRevenue = base / Math.pow(1 + rate, yearsBack);
      newRevenues[year] = Math.round(calculatedRevenue).toString();
    });
    
    setYearlyRevenue(newRevenues);
    setUseGrowthRate(false);
  };

  // Check if all years have revenue
  const allYearsHaveRevenue = yearRange.every(year => 
    yearlyRevenue[year] && parseFloat(yearlyRevenue[year]) > 0
  );

  // Get total revenue across all years
  const getTotalRevenue = (): number => {
    const revenues = yearRange.map(year => parseFloat(yearlyRevenue[year]) || 0);
    return revenues.reduce((sum, r) => sum + r, 0);
  };

  // Get average revenue per year
  const getAverageRevenue = (): number => {
    return getTotalRevenue() / yearRange.length;
  };

  // NOTE: Removed auto-select behavior - users should explicitly choose a template

  const fetchTemplates = async () => {
    try {
      const token = await getToken();
      const data = await api.get('/api/v1/templates/list', {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      setTemplates(data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleSelectTemplate = async (templateId: string) => {
    setLoading(true);
    try {
      const token = await getToken();
      const data = await api.get(`/api/v1/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      setSelectedTemplate(data);
      setShowDetails(true);
      
      // Auto-preview if all years have revenue
      if (allYearsHaveRevenue) {
        await fetchPreview(templateId, getAverageRevenue());
      }
    } catch (error) {
      console.error('Error fetching template:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPreview = async (templateId: string, avgRevenue: number) => {
    try {
      const token = await getToken();
      const data = await api.get(
        `/api/v1/templates/calculate-estimates?template_id=${templateId}&annual_revenue=${avgRevenue}`,
        { headers: { Authorization: `Bearer ${token}` }, showErrorToast: false }
      );
      setPreviewData(data);
    } catch (error) {
      console.error('Error calculating estimates:', error);
    }
  };

  const handleApply = async () => {
    if (!selectedTemplate || !allYearsHaveRevenue) return;
    
    setApplying(true);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      // Apply template for each year
      try {
        const data: AppliedTemplateData = await api.post(
          `/api/v1/templates/apply-multi-year?template_id=${selectedTemplate.id}`,
          { yearly_revenues: yearlyRevenue, coverage_year: coverageYear },
          { headers }
        );
        onApplyTemplate(data);
      } catch (multiYearError) {
        // Fallback to single-year apply if multi-year endpoint doesn't exist
        const avgRevenue = getAverageRevenue();
        const data: AppliedTemplateData = await api.post(
          `/api/v1/templates/apply?template_id=${selectedTemplate.id}`,
          { annual_revenue: avgRevenue, coverage_year: coverageYear, yearly_revenues: yearlyRevenue },
          { headers }
        );
        onApplyTemplate(data);
      }
      
      setShowDetails(false);
      if (compact) setIsExpanded(false);
    } catch (error) {
      console.error('Error applying template:', error);
      setErrorMessage('Failed to apply template. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  const formatPercent = (value: number) => `${value.toFixed(1)}%`;

  // Filter templates by search
  const filteredTemplates = templates.filter(t =>
    !searchQuery || 
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.business_types.some(bt => bt.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Compact header for applied template
  if (compact && appliedTemplateId && !isExpanded) {
    const appliedTemplate = templates.find(t => t.id === appliedTemplateId);
    return (
      <div className="rounded-xl p-4" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {appliedTemplate?.icon && <span className="text-2xl">{appliedTemplate.icon}</span>}
            <div>
              <p className="cs-label" style={{ color: 'var(--color-needs-human-text)' }}>Template Applied</p>
              <p className="cs-body" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{appliedTemplate?.name || 'Custom'}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setIsExpanded(true)}
              className="px-3 py-1.5 cs-body rounded-lg transition"
              style={{ color: 'var(--color-needs-human-text)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-needs-human-tint)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              Change
            </button>
            {onReset && (
              <button
                onClick={onReset}
                className="px-3 py-1.5 cs-body rounded-lg transition flex items-center gap-1"
                style={{ color: 'var(--color-text-muted)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <RotateCcw className="w-3 h-3" />
                Reset
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
      {/* Header */}
      <div
        className={`px-6 py-4 ${compact ? 'cursor-pointer' : ''}`}
        style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
        onClick={() => compact && setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6" />
            <div>
              <h3 className="cs-h3" style={{ color: 'var(--color-on-brand)' }}>Industry Templates</h3>
              <p className="cs-meta" style={{ color: 'var(--color-on-brand)', opacity: 0.85 }}>Pre-fill expense estimates based on industry benchmarks</p>
            </div>
          </div>
          {compact && (
            <button className="p-1 rounded-lg transition" style={{ background: 'transparent' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          )}
        </div>
      </div>

      {(!compact || isExpanded) && (
        <div className="p-6">
          {/* Template Selector */}
          {!showDetails && (
            <div className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search industries (e.g., restaurant, retail, tech)..."
                  className="w-full pl-10 pr-4 py-3 rounded-lg cs-body"
                  style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
                />
              </div>

              {/* Template Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {filteredTemplates.map(template => (
                  <button
                    key={template.id}
                    onClick={() => handleSelectTemplate(template.id)}
                    disabled={loading}
                    className={`p-4 rounded-xl text-left transition-all group relative ${loading ? 'opacity-50 cursor-wait' : ''}`}
                    style={
                      appliedTemplateId === template.id
                        ? { border: '2px solid var(--color-brand)', background: 'var(--color-needs-human-tint)' }
                        : { border: '2px solid var(--color-border)', background: 'var(--color-bg-surface)' }
                    }
                  >
                    {appliedTemplateId === template.id && (
                      <div className="absolute -top-2 -right-2 rounded-full p-1" style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}>
                        <Check className="w-3 h-3" />
                      </div>
                    )}
                    <span className="text-2xl mb-2 block">{template.icon}</span>
                    <h4 className="cs-body leading-tight" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{template.name}</h4>
                    <p className="cs-meta mt-1">
                      ~{template.gross_margin_avg}% margin
                    </p>
                  </button>
                ))}
              </div>

              {filteredTemplates.length === 0 && (
                <div className="text-center py-8" style={{ color: 'var(--color-text-muted)' }}>
                  <p>No templates found matching "{searchQuery}"</p>
                  <button
                    onClick={() => setSearchQuery('')}
                    className="mt-2 cs-body hover:underline"
                    style={{ color: 'var(--color-needs-human-text)' }}
                  >
                    Clear search
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Template Details */}
          {showDetails && selectedTemplate && (
            <div className="space-y-6">
              {/* Template Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-4xl">{selectedTemplate.icon}</span>
                  <div>
                    <h3 className="cs-h2">{selectedTemplate.name}</h3>
                    <p className="cs-meta">{selectedTemplate.description}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowDetails(false);
                    setSelectedTemplate(null);
                    setPreviewData(null);
                  }}
                  className="cs-body flex items-center gap-1"
                  style={{ color: 'var(--color-text-muted)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-text-secondary)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; }}
                >
                  ← Change template
                </button>
              </div>

              {/* Multi-Year Revenue Input */}
              <div className="rounded-lg p-4" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
                <div className="flex items-center justify-between mb-3">
                  <label className="block cs-label">
                    Enter Annual Revenue by Year
                  </label>
                  <button
                    onClick={() => setUseGrowthRate(!useGrowthRate)}
                    className="cs-meta"
                    style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}
                  >
                    {useGrowthRate ? '← Enter manually' : <span className="inline-flex items-center gap-1"><Zap size={12} /> Quick fill with growth rate</span>}
                  </button>
                </div>

                <p className="cs-meta mb-4">
                  The BI calculator needs multiple years of historical data to establish benchmarks. Enter revenue for each year.
                </p>

                {useGrowthRate ? (
                  <div className="rounded-lg p-4 space-y-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                    <p className="cs-body" style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}>Quick Fill Calculator</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block cs-meta mb-1">
                          Most Recent Year Revenue ({coverageYear})
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>$</span>
                          <input
                            type="number"
                            value={baseRevenue}
                            onChange={(e) => setBaseRevenue(e.target.value)}
                            placeholder="500,000"
                            className="w-full pl-8 pr-4 py-2 rounded-lg cs-figure"
                            style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block cs-meta mb-1">Annual Growth Rate (%)</label>
                        <div className="relative">
                          <input
                            type="number"
                            value={growthRate}
                            onChange={(e) => setGrowthRate(e.target.value)}
                            placeholder="5"
                            className="w-full px-4 py-2 pr-8 rounded-lg cs-figure"
                            style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>%</span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={applyGrowthRate}
                      disabled={!baseRevenue || parseFloat(baseRevenue) <= 0}
                      className="w-full py-2 rounded-lg cs-label disabled:opacity-50 transition"
                      style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                    >
                      Calculate & Fill All Years
                    </button>
                    <p className="cs-meta">
                      This will calculate historical revenues assuming {growthRate}% annual growth leading to your current revenue.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {yearRange.map((year, index) => (
                      <div key={year}>
                        <label className="block cs-meta mb-1" style={{ fontWeight: 500 }}>
                          {year}
                          {index === yearRange.length - 1 && (
                            <span className="ml-1" style={{ color: 'var(--color-needs-human-text)' }}>(Coverage Year)</span>
                          )}
                        </label>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 cs-meta">$</span>
                          <input
                            type="number"
                            value={yearlyRevenue[year]}
                            onChange={(e) => setYearlyRevenue({...yearlyRevenue, [year]: e.target.value})}
                            placeholder="500,000"
                            className="w-full pl-6 pr-2 py-2.5 rounded-lg cs-figure"
                            style={
                              yearlyRevenue[year] && parseFloat(yearlyRevenue[year]) > 0
                                ? { border: '1px solid var(--color-verified)', background: 'transparent', color: 'var(--color-text-primary)' }
                                : { border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }
                            }
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!useGrowthRate && (
                  <div className="mt-4">
                    {/* Show entered values summary */}
                    {allYearsHaveRevenue && (
                      <div className="mb-4 p-3 rounded-lg" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                        <p className="cs-body mb-2" style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}>Your Entered Revenue:</p>
                        <div className="flex flex-wrap gap-2">
                          {yearRange.map(year => (
                            <span key={year} className="cs-figure px-2 py-1 rounded" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                              {year}: {formatCurrency(parseInt(yearlyRevenue[year]))}
                            </span>
                          ))}
                          <span className="cs-figure px-2 py-1 rounded" style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 500 }}>
                            Total: {formatCurrency(getTotalRevenue())}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="flex justify-end">
                      <button
                        onClick={() => selectedTemplate && fetchPreview(selectedTemplate.id, getAverageRevenue())}
                        disabled={!allYearsHaveRevenue}
                        className="px-6 py-2.5 cs-label rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition"
                        style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                      >
                        {allYearsHaveRevenue ? 'Preview Monthly Breakdown' : `Enter all ${yearRange.length} years`}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Preview Results */}
              {previewData && (
                <div className="space-y-4">
                  {/* What Will Be Generated */}
                  <div className="rounded-lg p-4" style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}>
                    <h4 className="cs-h3 mb-2" style={{ color: 'var(--color-on-brand)' }}>{yearRange.length * 12} Months of Data Will Be Generated</h4>
                    <p className="cs-meta" style={{ color: 'var(--color-on-brand)', opacity: 0.85 }}>
                      This template will create monthly financial data for {yearRange[0]}-{yearRange[yearRange.length-1]}
                      ({yearRange.length} years × 12 months = {yearRange.length * 12} data points per category)
                    </p>
                  </div>

                  {/* Monthly Breakdown by Year */}
                  <div className="rounded-lg overflow-hidden" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                    <div className="px-4 py-3" style={{ background: 'var(--color-bg-elevated)', borderBottom: '1px solid var(--color-border-sub)' }}>
                      <h4 className="cs-h3">Monthly Revenue Breakdown (to be generated)</h4>
                    </div>
                    <div className="p-4">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr style={{ borderBottom: '1px solid var(--color-border-sub)' }}>
                              <th className="text-left py-2 px-2 cs-label">Year</th>
                              <th className="text-right py-2 px-2 cs-label">Annual Revenue</th>
                              <th className="text-right py-2 px-2 cs-label">Monthly Avg</th>
                              <th className="text-right py-2 px-2 cs-label">Monthly COGS</th>
                              <th className="text-right py-2 px-2 cs-label">Monthly Payroll</th>
                              <th className="text-right py-2 px-2 cs-label">Monthly Expenses</th>
                            </tr>
                          </thead>
                          <tbody>
                            {yearRange.map(year => {
                              const yearRev = parseFloat(yearlyRevenue[year] || '0');
                              const monthlyRev = yearRev / 12;
                              const monthlyCOGS = monthlyRev * (previewData.estimates.cogs / previewData.annual_revenue);
                              const monthlyPayroll = monthlyRev * (previewData.estimates.payroll / previewData.annual_revenue);
                              const monthlyExp = (previewData.estimates.rent + previewData.estimates.utilities +
                                                  previewData.estimates.insurance + previewData.estimates.marketing +
                                                  previewData.estimates.supplies + previewData.estimates.other) / 12;
                              return (
                                <tr
                                  key={year}
                                  style={{ borderBottom: '1px solid var(--color-border-faint)' }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                >
                                  <td className="py-2 px-2 cs-body" style={{ fontWeight: 500 }}>{year}</td>
                                  <td className="py-2 px-2 text-right cs-figure" style={{ color: 'var(--color-verified)', fontWeight: 500 }}>
                                    <FinancialValue value={yearRev} />
                                  </td>
                                  <td className="py-2 px-2 text-right cs-figure">
                                    <FinancialValue value={Math.round(monthlyRev)} />
                                  </td>
                                  <td className="py-2 px-2 text-right cs-figure" style={{ color: 'var(--color-needs-human-text)' }}>
                                    <FinancialValue value={Math.round(monthlyCOGS)} />
                                  </td>
                                  <td className="py-2 px-2 text-right cs-figure" style={{ color: 'var(--color-text-secondary)' }}>
                                    <FinancialValue value={Math.round(monthlyPayroll)} />
                                  </td>
                                  <td className="py-2 px-2 text-right cs-figure" style={{ color: 'var(--color-text-muted)' }}>
                                    <FinancialValue value={Math.round(monthlyExp)} />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot style={{ background: 'var(--color-bg-elevated)' }}>
                            <tr>
                              <td className="py-2 px-2 cs-body" style={{ fontWeight: 600 }}>Total ({yearRange.length} years)</td>
                              <td className="py-2 px-2 text-right cs-figure" style={{ color: 'var(--color-verified)', fontWeight: 600 }}>
                                <FinancialValue value={getTotalRevenue()} />
                              </td>
                              <td colSpan={4} className="py-2 px-2 text-right cs-meta">
                                {yearRange.length * 12} monthly records will be created
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Sample Monthly View for ALL Years */}
                  <div className="rounded-lg p-4" style={{ background: 'var(--color-bg-elevated)' }}>
                    <h4 className="cs-h3 mb-3">
                      Monthly Data Preview (All {yearRange.length} Years)
                    </h4>
                    <p className="cs-meta mb-4">
                      Your annual revenue inputs will be converted to monthly data. Here's what each year will look like:
                    </p>

                    {/* Show monthly preview for each year */}
                    <div className="space-y-4">
                      {yearRange.map(year => {
                        const yearRev = parseFloat(yearlyRevenue[year] || '0');
                        if (yearRev <= 0) return null;
                        const monthlyRev = yearRev / 12;

                        return (
                          <div key={year} className="rounded-lg p-3" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="cs-body" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{year}</span>
                              <span className="cs-meta">
                                Annual: {formatCurrency(yearRev)} → Monthly avg: {formatCurrency(Math.round(monthlyRev))}
                              </span>
                            </div>
                            <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-1">
                              {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, idx) => {
                                // Apply seasonal adjustment if template has it
                                const seasonalAdj = previewData.seasonal_adjustments ?
                                  (idx < 3 ? previewData.seasonal_adjustments.Q1 :
                                   idx < 6 ? previewData.seasonal_adjustments.Q2 :
                                   idx < 9 ? previewData.seasonal_adjustments.Q3 :
                                   previewData.seasonal_adjustments.Q4) || 1 : 1;
                                const adjustedRev = monthlyRev * seasonalAdj;
                                return (
                                  <div key={month} className="rounded p-1.5 text-center" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border-faint)' }}>
                                    <p className="text-[10px]" style={{ color: 'var(--color-text-dim)', fontWeight: 500 }}>{month}</p>
                                    <p className="text-[10px]" style={{ fontFamily: "'DM Mono', ui-monospace, monospace", fontWeight: 700, color: 'var(--color-verified)' }}>${Math.round(adjustedRev / 1000)}K</p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {previewData.seasonal_adjustments && (
                      <p className="cs-meta mt-3" style={{ color: 'var(--color-needs-human-text)' }}>
                        Seasonal adjustments applied: Q1 ({previewData.seasonal_adjustments.Q1}x),
                        Q2 ({previewData.seasonal_adjustments.Q2}x),
                        Q3 ({previewData.seasonal_adjustments.Q3}x),
                        Q4 ({previewData.seasonal_adjustments.Q4}x)
                      </p>
                    )}
                  </div>

                  {/* Template Ratios Used */}
                  <div className="rounded-lg p-4" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                    <h4 className="cs-h3 mb-3 flex items-center gap-2" style={{ color: 'var(--color-needs-human-text)' }}>
                      <Info className="w-4 h-4" />
                      Industry Ratios Applied (from {selectedTemplate?.name} template)
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="p-2 rounded" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                        <span className="cs-meta">COGS:</span>
                        <span className="cs-figure ml-2" style={{ fontWeight: 700 }}>{((previewData.estimates.cogs / previewData.annual_revenue) * 100).toFixed(1)}%</span>
                      </div>
                      <div className="p-2 rounded" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                        <span className="cs-meta">Payroll:</span>
                        <span className="cs-figure ml-2" style={{ fontWeight: 700 }}>{((previewData.estimates.payroll / previewData.annual_revenue) * 100).toFixed(1)}%</span>
                      </div>
                      <div className="p-2 rounded" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                        <span className="cs-meta">Gross Margin:</span>
                        <span className="cs-figure ml-2" style={{ fontWeight: 700, color: 'var(--color-verified)' }}>{previewData.calculated_margins.gross_margin_pct}%</span>
                      </div>
                      <div className="p-2 rounded" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                        <span className="cs-meta">Net Margin:</span>
                        <span className="cs-figure ml-2" style={{ fontWeight: 700, color: 'var(--color-needs-human-text)' }}>{previewData.calculated_margins.net_margin_pct}%</span>
                      </div>
                    </div>
                  </div>

                  {/* Industry Benchmarks */}
                  <div className="rounded-lg p-4" style={{ background: 'var(--color-bg-elevated)' }}>
                    <h4 className="cs-h3 mb-3">vs Industry Benchmarks</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center justify-between">
                        <span className="cs-meta">Gross Margin:</span>
                        <span className="cs-figure" style={{
                          fontWeight: 700,
                          color: previewData.calculated_margins.gross_margin_pct >= previewData.benchmarks.industry_gross_margin
                            ? 'var(--color-verified)' : 'var(--color-needs-human-text)'
                        }}>
                          {formatPercent(previewData.calculated_margins.gross_margin_pct)}
                          <span className="ml-1" style={{ color: 'var(--color-text-dim)', fontWeight: 400 }}>(avg: {formatPercent(previewData.benchmarks.industry_gross_margin)})</span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="cs-meta">Net Margin:</span>
                        <span className="cs-figure" style={{
                          fontWeight: 700,
                          color: previewData.calculated_margins.net_margin_pct >= previewData.benchmarks.industry_net_margin
                            ? 'var(--color-verified)' : 'var(--color-needs-human-text)'
                        }}>
                          {formatPercent(previewData.calculated_margins.net_margin_pct)}
                          <span className="ml-1" style={{ color: 'var(--color-text-dim)', fontWeight: 400 }}>(avg: {formatPercent(previewData.benchmarks.industry_net_margin)})</span>
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Tips */}
                  <div className="rounded-lg p-4" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                    <h4 className="cs-h3 mb-2 flex items-center gap-2" style={{ color: 'var(--color-needs-human-text)' }}>
                      Industry Tips
                    </h4>
                    <ul className="space-y-1 cs-body" style={{ color: 'var(--color-text-secondary)' }}>
                      {selectedTemplate.tips.slice(0, 3).map((tip, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="mt-1">•</span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
                <button
                  onClick={() => {
                    setShowDetails(false);
                    setSelectedTemplate(null);
                    setPreviewData(null);
                  }}
                  className="px-6 py-3 cs-label rounded-lg transition"
                  style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)', background: 'transparent' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleApply}
                  disabled={!previewData || !allYearsHaveRevenue || applying}
                  className="px-6 py-3 cs-label rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition"
                  style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                  onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
                >
                  {applying ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Applying...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Apply Template ({yearRange.length} Years)
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
